import { Component, Inject, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ToastController } from '@gruposaltaedu/liv-design-system/components';
import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { EMPTY, catchError, finalize, take } from 'rxjs';
import { KibanaSurveyModel } from 'src/app/core/models/kibana/kibana-survey';
import { EmotionStatus, SurveyModel } from 'src/app/core/models/survey-model';
import { KibanaService } from 'src/app/core/services/api/requests/kibana.service';
import { MODAL_DATA } from 'src/app/ui/components/modal/modal.component';
import { ModalService } from 'src/app/ui/components/modal/modal.service';

interface EmotionConfig {
  icon: string;
  color: {
    primary: string;
    secondary: string;
  };
}

interface FormGroupControl {
  emotion: FormControl<string | null>;
  tags: FormArray<
    FormGroup<{
      checked: FormControl<boolean | null>;
      value: FormControl<string | null>;
    }>
  >;
  comment: FormControl<string | null>;
}

@Component({
  selector: 'liv-survey-modal',
  templateUrl: './survey-modal.component.html',
  styleUrls: ['./survey-modal.component.scss'],
  standalone: false
})
export class SurveyModalComponent implements OnInit {
  readonly survey = signal<SurveyModel>(null);

  public savingSurveyResponse = false;

  public form: FormGroup<FormGroupControl>;

  public readonly EMOTION_CONFIG: Record<string, EmotionConfig> = {
    [EmotionStatus[EmotionStatus.very_bad]]: {
      icon: 'assets/icons/survey/bad.svg',
      color: {
        primary: '#d81d6a',
        secondary: '#e7a1b8'
      }
    },
    [EmotionStatus[EmotionStatus.bad]]: {
      icon: 'assets/icons/survey/sad.svg',
      color: {
        primary: '#ff5b07',
        secondary: '#f4b796'
      }
    },
    [EmotionStatus[EmotionStatus.good]]: {
      icon: 'assets/icons/survey/neutral.svg',
      color: {
        primary: '#ffbe12',
        secondary: '#fadc9f'
      }
    },
    [EmotionStatus[EmotionStatus.very_good]]: {
      icon: 'assets/icons/survey/happy.svg',
      color: {
        primary: '#40a299',
        secondary: '#88c6cd'
      }
    },
    [EmotionStatus[EmotionStatus.excellent]]: {
      icon: 'assets/icons/survey/very-happy.svg',
      color: {
        primary: '#4f89c8',
        secondary: '#9db9e0'
      }
    }
  };

  constructor(
    @Inject(MODAL_DATA) private data: SurveyModel,
    private modal: ModalService,
    private formBuilder: FormBuilder,
    private kibanaService: KibanaService,
    private toastService: ToastController,
    private language: I18nService
  ) {
    if (this.data) {
      this.survey.set(this.data);
    }
  }

  ngOnInit(): void {
    this.buildForm();
  }

  get emotion(): AbstractControl {
    return this.form.controls.emotion;
  }

  get comment(): AbstractControl {
    return this.form.controls.comment;
  }

  get tags(): FormArray {
    return this.form.controls.tags;
  }

  get isEmotionValid(): boolean {
    return this.emotion.valid;
  }

  private buildForm(): void {
    this.form = this.formBuilder.group({
      emotion: ['', [Validators.required]],
      tags: this.formBuilder.array(
        this.survey().tags.map(({ texto_tag }) => {
          return this.formBuilder.group({
            checked: [false],
            value: [texto_tag]
          });
        })
      ),
      comment: ['']
    });
  }

  private buildSurveyResponsePayload(): KibanaSurveyModel {
    const { emotion, tags, comment } = this.form.value;

    return {
      slug: this.survey().slug,
      rating: EmotionStatus[emotion as keyof typeof EmotionStatus],
      tags: tags.filter((tag) => tag.checked).map((tag) => tag.value),
      comment
    };
  }

  onEmotionChange(): void {
    this.tags.controls.map((tagControl) => {
      tagControl.patchValue({
        checked: false
      });
    });
  }

  handleFinishLater(): void {
    this.kibanaService.saveSurveySeeLater(this.survey().slug).subscribe(() => {
      this.modal.close();
    });
  }

  handleSubmit(): void {
    const surveyResponse = this.buildSurveyResponsePayload();

    this.savingSurveyResponse = true;

    this.kibanaService
      .saveSurveyResponse(surveyResponse)
      .pipe(
        take(1),
        catchError(() => {
          this.toastService.danger({
            message: this.language.instant('messages.save_survey_error')
          });

          return EMPTY;
        }),
        finalize(() => (this.savingSurveyResponse = true))
      )
      .subscribe(() => {
        this.modal.close();
        this.toastService.success({
          message: this.language.instant('messages.survey_thanks')
        });
      });
  }
}
